import { 
  loadSubstitutions, parseSubstitutionExpression, 
  tryParseSubstitution, hasSubstitutions,
  replaceByValues, convertValue,
} from 'shared/utils/substitution';
import { Type } from 'shared/utils/flowstep';
import { splitArrayToChunksBySize } from './utils';

export { tryParseSubstitution, hasSubstitutions };


export class SubstitutionError extends Error {
  constructor(message, { substitution, index }) {
    super(`Error at position ${index} in expression '${substitution}': ${message}`);

    this.index = index;
    this.substitution = substitution;
  }
}

export const Fake = {
  Pattern: 'FAKE_VALUE_',
  UInt: 100500,
};


export async function substituteQuery(query, queryType, { tablesLoader, rangesLoader, userInfoLoader }) {
  const substitutions = getSubstitutions(query, queryType);
  if (!substitutions) { return query; }

  const expressions = substitutions.map(x => x.expression);
  const results = await loadSubstitutions(expressions, { tablesLoader, rangesLoader });
  const withValues = substitutions.map((x, index) => {
    const { range, error } = results[index];
    if (error) {
      throw new SubstitutionError(error, x);
    }
    const values = range.map(cell => convertValue(cell));
    return { values, ...x };
  });

  return replaceSubstitutions(query, withValues);
}

export function substituteQueryWithFakes(query, queryType) {
  let hasSubstitutions = false;

  try {
    const s = getSubstitutions(query, queryType);
    if (!s) { return { query, hasSubstitutions}; }

    hasSubstitutions = true;
    let counter = 0;
    const getNextFake = (s) => [SubstitutionType.ComparisonOperator, SubstitutionType.LimitOperator, SubstitutionType.DateLiteral].includes(s.type)
      ? { value: Fake.UInt, needQuote: false }
      : { value: `${Fake.Pattern}${counter++}`, needQuote: true};
    
    const withValues = s.map(x => ({ values: [getNextFake(x)], ...x }));
    const convertedQuery = replaceSubstitutions(query, withValues);
    
    return { query: convertedQuery, hasSubstitutions };
  }
  catch (e) {
    console.log('Exception query: ', query);
    console.log('Error: ', e.message);
    return { query, hasSubstitutions };
  }
}

export function isSubstitutionPattern(string) {
  return /^{{.+}}$/.test(string);
}

export function toSubstitution({ address, tableName, columnName, user }) {
  const wrap = (x) => `{{${x}}}`;
  if (user) { return wrap(`$${user}`); }
  if (tableName && columnName) { return wrap(`@${tableName}[${columnName}]`); }
  return wrap(address);
}

const SUBST_SEPARATOR = ', ';
const SubstitutionType = {
  AsIs: 'as-is',
  Quote: 'quote',
  InOperator: 'in-operator',
  IncludeExcludeOpeartors: 'include-exclude-operators',
  LimitOperator: 'limit-operator',
  ComparisonOperator: 'comparison-operator',
  LikeOperator: 'like-operator',
  DateLiteral: 'date-literal',
};

function getSubstitutions(query, queryType) {
  const substitutions = [];
  let match = null;
  const regExp = Type.Sosl === queryType
    ? /(?<fullSubstitution>(?<leftQuote>\')?{{(?<substitution>[^{]+?)}}(?<rightQuote>\')?)/gi
    : /(?<fieldPlusOperator>(?:(?<field>[\w\.]+)\s*)?(?:(?<wordOperator>\w*?)\s+|(?<comparisonOperator>[\=\<\>\!]+)\s*))(?=(?<dateLiteral>\w+\:)?(?<fullSubstitution>(?:(?<leftQuote>\')(?<leftPercent>%?))?{{(?<substitution>.+?)}}(?:(?<rightPercent>%?)(?=\')(?<rightQuote>\')?)?))/gi;

  while ((match = regExp.exec(query))) {
    const s = getSubstitution(match);
    substitutions.push(s);
  }

  return substitutions.length ? substitutions : null;
}


function getSubstitution(match) {
  const { fieldPlusOperator, field, wordOperator, comparisonOperator, fullSubstitution, dateLiteral, leftQuote, leftPercent, substitution, rightPercent, rightQuote } = match.groups;
  const operator = wordOperator || comparisonOperator;
  const index = match.index + (fieldPlusOperator?.length ?? 0) + (dateLiteral?.length ?? 0);
  const forThrow = { index, substitution: fullSubstitution };

  if (!!leftQuote !== !!rightQuote) {
    throw new SubstitutionError('Leading or ending quote is missing', forThrow);
  }

  const hasNoPercents = !leftPercent && !rightPercent;
  const expression = parseSubstitutionExpression(substitution);
  if (expression.range?.address?.isUnbound) {
    throw new SubstitutionError(`Unbound range '${address.address}' cannot be used in substitutions`, forThrow);
  }
  
  const s = {
    type: getSubstitutionType(match.groups),
    index,
    length: fullSubstitution.length,
    field,
    operator,
    hasLeftPercent: !!leftPercent || hasNoPercents,
    hasRightPercent: !!rightPercent || hasNoPercents,
    substitution: fullSubstitution,
    expression,
    dateLiteral,
  };

  return s;
}

function getSubstitutionType({ comparisonOperator, wordOperator, dateLiteral, leftQuote }) {
  if (dateLiteral) {
    return SubstitutionType.DateLiteral;
  }

  const hasQuote = !!leftQuote;
  const wordOperatorUpper = wordOperator?.toUpperCase();

  const LIKE_OP = 'LIKE';
  const ops = {
    'IN': SubstitutionType.InOperator,
    'INCLUDES': SubstitutionType.IncludeExcludeOpeartors,
    'EXCLUDES': SubstitutionType.IncludeExcludeOpeartors,
    'LIMIT': SubstitutionType.LimitOperator,
    [LIKE_OP]: SubstitutionType.LikeOperator,
  };

  return hasQuote
    ? wordOperatorUpper === LIKE_OP ? SubstitutionType.LikeOperator : SubstitutionType.Quote
    : comparisonOperator
      ? SubstitutionType.ComparisonOperator
      : ops[wordOperatorUpper] ?? SubstitutionType.AsIs;
}

function replaceSubstitutions(query, substitutions) {
  const quoteValues = (values) => values.map(({ value }) => `'${value}'`).join(SUBST_SEPARATOR);
  const likeDecorator = (value, { hasLeftPercent, hasRightPercent }) =>
    `${hasLeftPercent ? '%' : ''}${value}${hasRightPercent ? '%' : ''}`;

  const getSubstitutionValue = (substitution) => {
    let values = substitution.values.filter(({ value }) => typeof(value) !== 'string' || !!value.length);
    values = values.length ? values : [{ value: '', needQuote: true }];

    switch (substitution.type) {
      case SubstitutionType.Quote: return values.length > 1
          ? `'(${quoteValues(values)})'`
          : quoteValues(values);

      case SubstitutionType.IncludeExcludeOpeartors:
      case SubstitutionType.InOperator: return `(${quoteValues(values)})`;

      case SubstitutionType.DateLiteral:
      case SubstitutionType.LimitOperator: {
        const { operator, dateLiteral } = substitution;
        const op = dateLiteral || operator;

        if (values.length > 1) {
          throw new SubstitutionError(`Only one value can be used with '${op}' operator`, substitution);
        }

        const converted = +values[0].value;
        if (isNaN(converted) || converted < 0) {
          throw new SubstitutionError(`'${values[0].value}' isn't an unsigned integer value to use with '${op}' operator`, substitution);
        }

        return `${converted}`;
      }

      case SubstitutionType.LikeOperator: {
        return `'${values.map(({ value }, index) =>
          index === 0
            ? likeDecorator(value, substitution)
            : `' OR ${substitution.field} LIKE '${likeDecorator(value, substitution)}`
        ).join('')}'`;
      }

      case SubstitutionType.ComparisonOperator:
      case SubstitutionType.AsIs:
      default: return values.map(x => x.value).join(SUBST_SEPARATOR);
    }
  }

  return replaceByValues(query, substitutions
    .map(substitution => ({
      value: getSubstitutionValue(substitution),
      index: substitution.index,
      length: substitution.length
    }))
  );
}

export function splitQuery(query, lengthLimit) {
  const queryRegex = /(?<queryStart>.*IN\s*)(?<valuesString>\(.*\))\s*$/mi;
  const match = query.match(queryRegex);
  if (!match) { return }

  const { queryStart, valuesString } = match.groups;


  const values = valuesString
    //remove the parentheses
    .slice(1, valuesString.length - 1)
    .split(',');
  const parenthesesLength = 2;
  const VALUES_MAX_LENGTH = lengthLimit - queryStart.length - parenthesesLength;

  const valuesChunks = splitArrayToChunksBySize(values, VALUES_MAX_LENGTH, item => item.length + 1);
  let queries = null;

  if (valuesChunks) {
    queries = valuesChunks.map(chunk => `${queryStart}(${chunk.join(',')})`);
  }
  
  return queries;
}